import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Search, BookOpen, Settings2, Sparkles, X,
  Layers, AlignJustify, Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { SopViewerDialog } from "@/components/sops/sop-viewer-dialog";
import { cn } from "@/lib/utils";

type SOP = {
  id: string;
  title: string;
  category: string | null;
  audience: "manus" | "humans" | "both";
  status: "draft" | "published";
  visibility: "all" | "selected";
  createdAt: string;
  updatedAt: string;
};

type Category = { id: string; name: string; color: string };

// ── SOP card ──────────────────────────────────────────────────────────────────
function SopCard({
  sop,
  categoryColor,
  aiRank,
  onClick,
}: {
  sop: SOP;
  categoryColor?: string;
  aiRank?: number;
  onClick: () => void;
}) {
  return (
    <Card
      className="cursor-pointer hover:border-primary transition-all overflow-hidden group relative"
      onClick={onClick}
      data-testid={`card-sop-${sop.id}`}
    >
      {aiRank !== undefined && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-[10px] font-semibold text-primary">{aiRank}</span>
        </div>
      )}
      <div className="p-5 h-full flex flex-col">
        <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors mb-3">
          {sop.title}
        </h3>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {sop.category ? (
              <Badge
                variant="secondary"
                className="bg-secondary/50 gap-1.5 shrink-0"
              >
                {categoryColor && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: categoryColor }}
                  />
                )}
                {sop.category}
              </Badge>
            ) : null}
            {sop.visibility === "selected" && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 shrink-0"
                data-testid={`badge-shared-with-you-${sop.id}`}
              >
                Shared with you
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {format(new Date(sop.updatedAt), "MMM d")}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ── Category section header ───────────────────────────────────────────────────
function CategorySection({
  name,
  color,
  children,
}: {
  name: string;
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {color && (
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
        )}
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {name}
        </h2>
        <div className="flex-1 h-px bg-border" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SopsPage() {
  const { isAdmin, isMasterAdmin } = useAuth();
  const { toast } = useToast();
  const canManage = isAdmin || isMasterAdmin;

  // ── Search state ─────────────────────────────────────────────────────────
  const [searchMode, setSearchMode] = useState<"text" | "ai">("text");
  const [textSearch, setTextSearch] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [committedTextSearch, setCommittedTextSearch] = useState("");
  const [aiResultIds, setAiResultIds] = useState<string[] | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // ── Filter & group ───────────────────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [groupByCategory, setGroupByCategory] = useState(false);

  // ── Viewer ───────────────────────────────────────────────────────────────
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewingSopId, setViewingSopId] = useState<string | null>(null);

  // ── Data: SOPs ───────────────────────────────────────────────────────────
  const { data: allSops = [], isLoading } = useQuery<SOP[]>({
    queryKey: ["/api/sops", committedTextSearch, activeCategory, "published"],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (committedTextSearch) params.append("search", committedTextSearch);
      if (activeCategory !== "all") params.append("category", activeCategory);
      params.append("status", "published");
      const res = await fetch(`/api/sops?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch SOPs");
      return res.json();
    },
  });

  // ── Data: categories (scoped to SOPs only) ───────────────────────────────
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories", "sops"],
    queryFn: async () => {
      const res = await fetch("/api/categories?scope=sops");
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
  });

  const categoryColorMap = Object.fromEntries(categories.map((c) => [c.name, c.color]));

  // ── AI search ────────────────────────────────────────────────────────────
  const aiSearch = useMutation({
    mutationFn: async (query: string) => {
      const res = await fetch("/api/sops/ai-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error("AI search failed");
      return res.json() as Promise<{ ids: string[] }>;
    },
    onSuccess: (data) => {
      setAiResultIds(data.ids);
    },
    onError: () => {
      toast({ title: "AI search failed — please try again", variant: "destructive" });
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const runTextSearch = useCallback(() => {
    setAiResultIds(null);
    setCommittedTextSearch(textSearch);
  }, [textSearch]);

  const runAiSearch = useCallback(() => {
    if (!aiQuery.trim()) return;
    setCommittedTextSearch("");
    aiSearch.mutate(aiQuery.trim());
  }, [aiQuery, aiSearch]);

  const clearSearch = useCallback(() => {
    setTextSearch("");
    setAiQuery("");
    setCommittedTextSearch("");
    setAiResultIds(null);
    aiSearch.reset();
    setTimeout(() => textInputRef.current?.focus(), 0);
  }, [aiSearch]);

  const switchToAi = useCallback(() => {
    setSearchMode("ai");
    setTextSearch("");
    setCommittedTextSearch("");
    setAiResultIds(null);
  }, []);

  const switchToText = useCallback(() => {
    setSearchMode("text");
    setAiQuery("");
    setAiResultIds(null);
    aiSearch.reset();
    setTimeout(() => textInputRef.current?.focus(), 0);
  }, [aiSearch]);

  // ── Derived: display SOPs ────────────────────────────────────────────────
  let displaySops = allSops;
  let aiRankMap: Record<string, number> = {};

  if (aiResultIds !== null) {
    // AI mode: show only the AI-matched SOPs, in ranked order, from all SOPs
    // We need a separate "all SOPs" list for AI mode (no text filter)
    aiRankMap = Object.fromEntries(aiResultIds.map((id, i) => [id, i + 1]));
    displaySops = allSops
      .filter((s) => aiResultIds.includes(s.id))
      .sort((a, b) => (aiRankMap[a.id] ?? 999) - (aiRankMap[b.id] ?? 999));
  }

  // ── Group by category ────────────────────────────────────────────────────
  interface Group {
    name: string;
    color?: string;
    sops: SOP[];
  }

  const grouped: Group[] = (() => {
    if (!groupByCategory) return [];
    const map = new Map<string, SOP[]>();
    for (const sop of displaySops) {
      const key = sop.category ?? "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(sop);
    }
    const result: Group[] = [];
    // Ordered by category definition first
    for (const cat of categories) {
      if (
        (activeCategory === "all" || activeCategory === cat.name) &&
        map.has(cat.name)
      ) {
        result.push({ name: cat.name, color: cat.color, sops: map.get(cat.name)! });
      }
    }
    // Categories on SOPs that aren't in the category list
    for (const [key, sops] of map.entries()) {
      if (key === "__none__") continue;
      if (!categories.some((c) => c.name === key)) {
        result.push({ name: key, sops });
      }
    }
    // Uncategorized last
    if (map.has("__none__")) {
      result.push({ name: "Uncategorized", sops: map.get("__none__")! });
    }
    return result;
  })();

  const hasResults = displaySops.length > 0;
  const isSearching = aiSearch.isPending;
  const aiActive = aiResultIds !== null;

  return (
    <div className="w-full min-h-screen p-4 md:p-6 space-y-6">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary/10 rounded-lg hidden md:block">
            <BookOpen className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">SOPs &amp; FAQs</h1>
            <p className="text-muted-foreground">Guidelines and processes for how we work</p>
          </div>
        </div>
        {canManage && (
          <Link href="/admin/sops">
            <Button variant="outline" data-testid="button-manage-sops-link">
              <Settings2 className="w-4 h-4 mr-2" />
              Manage SOPs
            </Button>
          </Link>
        )}
      </div>

      {/* ── Search + filters ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="space-y-3">
            {/* Mode tabs */}
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
              <button
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  searchMode === "text"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={switchToText}
                type="button"
                data-testid="button-search-mode-text"
              >
                <Search className="w-3.5 h-3.5" />
                Keyword
              </button>
              <button
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  searchMode === "ai"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={switchToAi}
                type="button"
                data-testid="button-search-mode-ai"
              >
                <Sparkles className="w-3.5 h-3.5" />
                AI Search
              </button>
            </div>

            {/* Search input area */}
            {searchMode === "text" ? (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={textInputRef}
                    placeholder="Search by title or content…"
                    value={textSearch}
                    onChange={(e) => setTextSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runTextSearch()}
                    className="pl-9 pr-9"
                    data-testid="input-search-sops-viewer"
                  />
                  {textSearch && (
                    <button
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={clearSearch}
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Button onClick={runTextSearch} data-testid="button-search-sops">
                  Search
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Textarea
                  placeholder="Describe what you're looking for — e.g. 'How do I schedule a meeting with a client?'"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runAiSearch();
                  }}
                  rows={2}
                  className="resize-none"
                  data-testid="textarea-ai-search-sops"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Describe the topic in plain English — AI will find the most relevant SOPs.
                    {aiActive && (
                      <button
                        className="ml-2 text-primary underline"
                        onClick={clearSearch}
                        type="button"
                      >
                        Clear results
                      </button>
                    )}
                  </p>
                  <Button
                    onClick={runAiSearch}
                    disabled={!aiQuery.trim() || isSearching}
                    data-testid="button-ai-search-sops"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Category filter chips + Group toggle */}
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
                <button
                  className={cn(
                    "flex-shrink-0 px-3 py-1 rounded-full text-sm font-medium border transition-colors",
                    activeCategory === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                  )}
                  onClick={() => setActiveCategory("all")}
                  type="button"
                  data-testid="chip-category-all"
                >
                  All
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={cn(
                      "flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border transition-colors",
                      activeCategory === cat.name
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                    )}
                    onClick={() => setActiveCategory(activeCategory === cat.name ? "all" : cat.name)}
                    type="button"
                    data-testid={`chip-category-${cat.id}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor:
                          activeCategory === cat.name ? "currentColor" : cat.color,
                      }}
                    />
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Group by toggle */}
              <button
                className={cn(
                  "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                  groupByCategory
                    ? "bg-primary/10 border-primary/30 text-primary"
                    : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30",
                )}
                onClick={() => setGroupByCategory((v) => !v)}
                type="button"
                data-testid="button-group-by-category"
                title={groupByCategory ? "Ungroup" : "Group by category"}
              >
                {groupByCategory ? (
                  <Layers className="w-4 h-4" />
                ) : (
                  <AlignJustify className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {groupByCategory ? "Grouped" : "Group"}
                </span>
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* ── AI result summary ──────────────────────────────────────────── */}
          {aiActive && (
            <div className="mb-4 flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-primary shrink-0" />
              {displaySops.length === 0 ? (
                <span className="text-muted-foreground">
                  No SOPs matched your query. Try rewording it.
                </span>
              ) : (
                <span>
                  <span className="font-medium text-primary">{displaySops.length}</span>
                  {" "}SOP{displaySops.length !== 1 ? "s" : ""} found for &ldquo;
                  <span className="italic">{aiQuery}</span>&rdquo;
                </span>
              )}
            </div>
          )}

          {/* ── Loading ────────────────────────────────────────────────────── */}
          {isLoading || isSearching ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>

          ) : !hasResults ? (
            /* ── Empty ─────────────────────────────────────────────────────── */
            <div className="text-center py-16 text-muted-foreground border rounded-lg border-dashed">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-lg font-medium">No SOPs found</p>
              <p className="text-sm">
                {aiActive
                  ? "Try rephrasing your query, or switch to Keyword search."
                  : "Try adjusting your search or category filter."}
              </p>
            </div>

          ) : groupByCategory ? (
            /* ── Grouped view ───────────────────────────────────────────────── */
            <div className="space-y-8">
              {grouped.map((group) => (
                <CategorySection
                  key={group.name}
                  name={group.name}
                  color={group.color}
                >
                  {group.sops.map((sop) => (
                    <SopCard
                      key={sop.id}
                      sop={sop}
                      categoryColor={categoryColorMap[sop.category ?? ""] }
                      aiRank={aiActive ? aiRankMap[sop.id] : undefined}
                      onClick={() => { setViewingSopId(sop.id); setIsViewerOpen(true); }}
                    />
                  ))}
                </CategorySection>
              ))}
            </div>

          ) : (
            /* ── Flat grid ──────────────────────────────────────────────────── */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displaySops.map((sop) => (
                <SopCard
                  key={sop.id}
                  sop={sop}
                  categoryColor={categoryColorMap[sop.category ?? ""]}
                  aiRank={aiActive ? aiRankMap[sop.id] : undefined}
                  onClick={() => { setViewingSopId(sop.id); setIsViewerOpen(true); }}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SopViewerDialog
        open={isViewerOpen}
        onOpenChange={setIsViewerOpen}
        sopId={viewingSopId}
      />
    </div>
  );
}
